/**
 * Dates as the provenance record holds them.
 *
 * A chain is dated to whatever precision its author could honestly give — a
 * year, a month, a day, or nothing — so this slice is the three-part date the
 * selects hold, the round trip between that and the string the record stores,
 * and the words a range or an act's timestamp is rendered as. Nothing here
 * knows what an entry is; it only knows what a date is allowed to say.
 */
import { formatDay } from "../lib/dates.ts";
import {
  EARLIEST_PROVENANCE_YEAR,
  MONTH_NAMES,
  PROVENANCE_COPY,
} from "./provenance.constants.ts";

/**
 * A date as the three selects hold it. Every part is blank-able, and blank is
 * the honest answer for a chain nobody dated to the day.
 */
export interface DateParts {
  day: string;
  month: string;
  year: string;
}

export const BLANK_DATE: DateParts = { day: "", month: "", year: "" };

/**
 * '12 June 1938', 'June 1938', '1938', ''.
 *
 * The shortest form the parts justify. A year with no month is not a January,
 * so nothing is filled in on the author's behalf — the record says only what
 * was chosen.
 *
 * Year carries the whole date: without one, a day and a month name nothing, so
 * the result is empty rather than a fragment the timeline would have to print.
 */
export function formatDateParts(parts: DateParts): string {
  if (parts.year === "") return "";
  if (parts.month === "") return parts.year;
  if (parts.day === "") return `${parts.month} ${parts.year}`;
  return `${parts.day} ${parts.month} ${parts.year}`;
}

/**
 * The inverse of `formatDateParts`, and total: anything it does not recognise
 * comes back blank.
 *
 * Failing to blank rather than to a guess is the point. A string this cannot
 * read is a string nobody chose in these selects, and showing an approximate
 * reading of it would put a date in the record that no one asserted.
 */
export function parseDateParts(value: string): DateParts {
  const words = value.trim().split(/\s+/).filter(Boolean);
  const year = words.at(-1) ?? "";
  if (!/^\d{4}$/.test(year)) return BLANK_DATE;

  const month = words.length > 1 ? (words.at(-2) ?? "") : "";
  if (month !== "" && !MONTH_NAMES.includes(month as (typeof MONTH_NAMES)[number])) {
    return BLANK_DATE;
  }

  const day = words.length > 2 ? (words[0] ?? "") : "";
  if (words.length > 3) return BLANK_DATE;
  if (day !== "" && !/^\d{1,2}$/.test(day)) return BLANK_DATE;
  if (day !== "" && Number(day) > daysInMonth(month, year)) return BLANK_DATE;

  return { day, month, year };
}

/**
 * How long the chosen month is, leap year included.
 *
 * 31 when the month is not yet chosen: the day select is unreachable until it
 * is, so the fallback only ever feeds a list nobody can see.
 */
export function daysInMonth(month: string, year: string): number {
  const index = MONTH_NAMES.indexOf(month as (typeof MONTH_NAMES)[number]);
  if (index < 0) return 31;
  if (index !== 1) return [31, 0, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][index] ?? 31;

  const y = Number(year);
  if (!Number.isInteger(y)) return 29;
  // The century rule. 1900 was not a leap year, and provenance reaches back
  // far enough to hit the ones that are not.
  return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0 ? 29 : 28;
}

/**
 * The parts with anything the change invalidated dropped.
 *
 * March 31st with the month moved to April is not April 31st and not April
 * 30th either — it is a day the author has not chosen yet. Clearing it asks
 * again; clamping it would answer for them.
 *
 * Year and month cascade the same way: clearing a year clears the whole date,
 * because a month with no year names nothing.
 */
export function clampDateParts(parts: DateParts): DateParts {
  if (parts.year === "") return BLANK_DATE;
  if (parts.month === "") return { day: "", month: "", year: parts.year };
  if (parts.day !== "" && Number(parts.day) > daysInMonth(parts.month, parts.year)) {
    return { ...parts, day: "" };
  }
  return parts;
}

/** Today's year. Named so the year list can be tested without mocking a clock. */
export function thisYear(): number {
  return new Date().getFullYear();
}

/** Newest first, so the years anyone actually types are at the top. */
export function yearOptions(currentYear: number = thisYear()): string[] {
  const years: string[] = [];
  for (let year = currentYear; year >= EARLIEST_PROVENANCE_YEAR; year -= 1) {
    years.push(String(year));
  }
  return years;
}

/**
 * '2001–2003', '1979–Present', 'Until 2003', 'Date unknown'.
 *
 * Never a bare dash. A range with neither end is a fact the record does not
 * have, and rendering '–' would make a missing date look like a rendering bug.
 */
export function describeDateRange(start: string, end: string): string {
  const from = start.trim();
  const to = end.trim();

  if (from === "" && to === "") return PROVENANCE_COPY.dateUnknown;
  if (from === "") return `Until ${to}`;
  if (to === "") return `${from}–${PROVENANCE_COPY.present}`;
  if (from === to) return from;
  return `${from}–${to}`;
}

/**
 * '12 Jan 2026'.
 *
 * en-GB and UTC, both fixed. A date that read differently for two people
 * looking at the same attestation would be two facts, and the record has one.
 */
export function formatActDate(iso: string): string {
  return formatDay(iso, { utc: true, fallback: "—" });
}
